import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FiCheck, FiChevronRight, FiEdit2 } from 'react-icons/fi';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { useColorScheme } from '../../contexts/ColorSchemeContext';
import baseline from '../../app-ui/baseline';
import {
  archiveGoalDefinitions,
  archiveGoalTargets,
  archiveProgressValue,
} from './generated/archiveGoals';
import { archivePeriodTitle, archiveTranslate, goalCopy } from './archiveModel';
import useMyArchive from './useMyArchive';
import ArchiveGoalEditor from './ArchiveGoalEditor';
import './MyArchive.css';
export default function MyArchive({ profile }) {
  const { locale } = useWebLocale(),
    { resolvedMode } = useColorScheme(),
    t = archiveTranslate(locale),
    archive = useMyArchive(profile);
  const [params, setParams] = useSearchParams(),
    location = useLocation(),
    navigate = useNavigate();
  const [expanded, setExpanded] = useState(false),
    probe = useRef();
  useLayoutEffect(() => {
    const measure = () => {
      if (probe.current) setExpanded(probe.current.getBoundingClientRect().height >= 22.4);
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (probe.current) observer?.observe(probe.current);
    return () => observer?.disconnect();
  }, [archive.enabled]);
  if (!archive.enabled) return null;
  const colors = baseline.tokens[resolvedMode === 'dark' ? 'darkColors' : 'lightColors'];
  const style = {
    '--archive-text': colors.text,
    '--archive-secondary': colors.textSecondary,
    '--archive-muted': colors.textMuted,
    '--archive-bg': colors.background,
    '--archive-surface': colors.surface,
    '--archive-soft': colors.surfaceSoft,
    '--archive-muted-bg': colors.surfaceMuted,
    '--archive-border': colors.divider,
    '--archive-selected': colors.accentSelected,
  };
  const data = archive.data,
    targets = archiveGoalTargets(data),
    active = archiveGoalDefinitions.filter((x) => targets[x.type] > 0),
    completed =
      data &&
      active.length > 0 &&
      active.every((x) => archiveProgressValue(data, x.type) >= targets[x.type]);
  const edit = () => {
    const next = new URLSearchParams(params);
    next.set('goal', archive.period);
    setParams(next, {
      state: { ...location.state, archiveGoalOpened: true, archiveGoalSourceKey: location.key },
    });
  };
  const close = () => {
    if (location.state?.archiveGoalOpened) navigate(-1);
    else {
      const next = new URLSearchParams(params);
      next.delete('goal');
      setParams(next, { replace: true });
    }
  };
  const goal = params.get('goal');
  return (
    <section
      className={`pp-archive ${expanded ? 'is-expanded' : ''}`}
      style={style}
      aria-label={t('my.musicArchive')}
    >
      <span className="pp-archive-font-probe" ref={probe} aria-hidden="true" />
      <h2>{t('my.musicArchive')}</h2>
      {archive.loading ? (
        <p role="status">{t('archive.homeLoading')}</p>
      ) : !data ? (
        <div className="pp-archive-overview">
          <p role="alert">{t('archive.loadError')}</p>
          <button className="pp-archive-retry" onClick={archive.retry}>
            {t('common.retry')}
          </button>
        </div>
      ) : (
        <div className="pp-archive-overview">
          <div role="tablist" aria-label={t('my.musicArchive')} className="pp-archive-period">
            {['WEEK', 'MONTH'].map((option) => (
              <button
                role="tab"
                key={option}
                aria-selected={archive.period === option}
                onClick={() => archive.setPeriod(option)}
              >
                {t(option === 'WEEK' ? 'archive.thisWeek' : 'archive.thisMonth')}
              </button>
            ))}
          </div>
          <div className="pp-archive-heading">
            <div>
              <h3>{archivePeriodTitle(data, locale, t)}</h3>
              <small>{t('archive.countingNote')}</small>
            </div>
            <button
              className="pp-archive-edit"
              data-profile-focus-key="archive-edit"
              onClick={edit}
            >
              <FiEdit2 size={13} />
              {t(data.goal.isConfigured ? 'archive.editGoal' : 'archive.setGoal')}
            </button>
          </div>
          {data.goal.isConfigured && (
            <div className="pp-archive-metrics">
              {active.map((def, index) => {
                const copy = goalCopy(def.type),
                  current = archiveProgressValue(data, def.type),
                  target = targets[def.type],
                  done = current >= target;
                return (
                  <div
                    key={def.type}
                    className={`pp-archive-metric ${done ? 'is-complete' : ''} ${active.length === 1 || (active.length === 3 && index === 0) ? 'is-wide' : ''}`}
                  >
                    <div>
                      <div className="pp-archive-metric-heading">
                        <small>{t(`archive.goal.${copy.label}`)}</small>
                        {done && (
                          <span className="pp-archive-badge">
                            <FiCheck size={11} />
                            {t('archive.completed')}
                          </span>
                        )}
                      </div>
                      <div className="pp-archive-value">
                        {current}
                        <small>
                          {' '}
                          / {target}
                          {t(`archive.goal.${copy.suffix}`)}
                        </small>
                      </div>
                    </div>
                    <div
                      className="pp-archive-progress"
                      role="progressbar"
                      aria-label={t(`archive.goal.${copy.label}`)}
                      aria-valuemin={0}
                      aria-valuemax={target}
                      aria-valuenow={Math.min(current, target)}
                      aria-valuetext={`${current} / ${target}`}
                    >
                      <span style={{ width: `${Math.min(current / target, 1) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {completed && (
            <button
              className="pp-archive-complete-action"
              data-profile-focus-key="archive-complete"
              onClick={edit}
            >
              <span className="pp-archive-check">
                <FiCheck size={16} />
              </span>
              <span>
                <strong>
                  {t('archive.allGoalsComplete', {
                    period: t(
                      data.periodType === 'WEEK' ? 'archive.thisWeek' : 'archive.thisMonth',
                    ),
                  })}
                </strong>
                <small>{t('archive.raiseGoalPrompt')}</small>
              </span>
              <small>{t('archive.raiseGoal')}</small>
              <FiChevronRight size={16} />
            </button>
          )}
          {archive.error && (
            <div role="alert" className="pp-archive-error">
              {t('archive.loadError')}
              <button onClick={archive.retry}>{t('common.retry')}</button>
            </div>
          )}
          <Link className="pp-archive-records" to="/mypage/ratings">
            {t('archive.viewAll')}
            <FiChevronRight size={17} />
          </Link>
        </div>
      )}
      {['WEEK', 'MONTH'].includes(goal) && (
        <ArchiveGoalEditor
          key={`${archive.id}-${profile.nickname}`}
          id={archive.id}
          nickname={profile.nickname}
          initialPeriod={goal}
          expanded={expanded}
          t={t}
          style={style}
          onClose={close}
        />
      )}
    </section>
  );
}
